import React, { Component } from 'react';
import { experienceRef } from '../config/firebase';
import { onValue, set, child, update, remove } from 'firebase/database';
import PageLoader from './PageLoader';
import {
    LuBriefcase,
    LuImagePlus,
    LuX,
    LuPlus,
    LuSave,
    LuTrash2,
    LuMapPin,
    LuCalendar,
    LuBuilding2,
    LuPencilLine,
    LuSparkles
} from 'react-icons/lu';
import Cropper from 'react-easy-crop';
import { getCroppedImg, dataUrlToBlob, extensionForMime } from '../actions/cropUtils';
import { supabase, EXPERIENCE_BUCKET, storagePathFromUrl } from '../config/supabase';
import { showToast } from '../actions/toastBus';
import '../stylesheet/style.css';
import '../stylesheet/Experience.css';

const isCurrentRole = (end) => !end || /present|current/i.test(end);

const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

const blankExperience = { cname: "", company: "", title: "", role: "", location: "", type: "", mode: "", start: "", end: "" };

// Parses loose free-text dates like "Jan 2022", "January 2022" or "2022"
// into a {y, m} pair so we can compute LinkedIn-style "X yrs Y mos" spans.
// Returns null when the text isn't parseable - callers fall back to
// showing the raw text with no computed duration.
const parseMonthYear = (str) => {
    if (!str) return null;
    const s = str.trim().toLowerCase();
    const monthYear = s.match(/([a-z]{3,9})\.?\s+(\d{4})/);
    if (monthYear) {
        const idx = MONTHS.findIndex((m) => monthYear[1].startsWith(m));
        if (idx !== -1) return { y: parseInt(monthYear[2], 10), m: idx };
    }
    const yearOnly = s.match(/^(\d{4})$/);
    if (yearOnly) return { y: parseInt(yearOnly[1], 10), m: 0 };
    return null;
};

const formatSpan = (months) => {
    if (months == null || months < 0) return null;
    const yrs = Math.floor(months / 12);
    const mos = months % 12;
    const parts = [];
    if (yrs) parts.push(`${yrs} yr${yrs > 1 ? 's' : ''}`);
    if (mos || !yrs) parts.push(`${mos} mo${mos !== 1 ? 's' : ''}`);
    return parts.join(' ');
};

// Duration for a single role.
const roleDuration = (start, end) => {
    const s = parseMonthYear(start);
    const now = new Date();
    const e = isCurrentRole(end) ? { y: now.getFullYear(), m: now.getMonth() } : parseMonthYear(end);
    if (!s || !e) return null;
    return formatSpan((e.y * 12 + e.m) - (s.y * 12 + s.m));
};

// Groups a latest-first list of {experience, id} entries by company so
// multiple roles at the same employer nest under one nameplate, LinkedIn
// style. Entries without a `company` value stand alone (backwards
// compatible with existing data that only has cname/title/role).
const groupByCompany = (ordered) => {
    const groups = [];
    const indexByKey = new Map();
    ordered.forEach((entry) => {
        const key = entry.experience.company ? entry.experience.company.trim().toLowerCase() : `__solo_${entry.id}`;
        if (indexByKey.has(key)) {
            groups[indexByKey.get(key)].entries.push(entry);
        } else {
            indexByKey.set(key, groups.length);
            groups.push({ key, entries: [entry] });
        }
    });
    return groups;
};

// Earliest start - latest end (or Present) across every role at a company,
// plus the total computed duration, for the group's nameplate row.
const groupSpan = (entries) => {
    const roles = entries.map((e) => e.experience);
    const anyCurrent = roles.some((r) => isCurrentRole(r.end));
    let earliest = null, earliestRaw = null, latest = null, latestRaw = null;
    roles.forEach((r) => {
        const s = parseMonthYear(r.start);
        if (s && (!earliest || s.y * 12 + s.m < earliest.y * 12 + earliest.m)) { earliest = s; earliestRaw = r.start; }
        if (!anyCurrent) {
            const e = parseMonthYear(r.end);
            if (e && (!latest || e.y * 12 + e.m > latest.y * 12 + latest.m)) { latest = e; latestRaw = r.end; }
        }
    });
    const now = new Date();
    const end = anyCurrent ? { y: now.getFullYear(), m: now.getMonth() } : latest;
    const rangeText = `${earliestRaw || roles[roles.length - 1].start} — ${anyCurrent ? 'Present' : (latestRaw || roles[0].end)}`;
    const duration = earliest && end ? formatSpan((end.y * 12 + end.m) - (earliest.y * 12 + earliest.m)) : null;
    return { rangeText, duration, anyCurrent };
};

export default class Experience extends Component {

    constructor(props) {
        super(props);
        this.state = {
            experiences: [],
            isLoggedIn: false,
            contentLoading: true,
            cropModalOpen: false,
            uploadedImage: null,
            uploadedImageType: null,
            croppedAreaPixels: null,
            crop: { x: 0, y: 0 },
            zoom: 1,
            draftExperience: { ...blankExperience },
            // null = the popup is adding a new experience; otherwise the
            // id (real Firebase index) of the experience being edited.
            editingId: null,
            modalOpen: false,
            modalClosing: false
        };
    };

    componentDidMount() {
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(experienceRef, (snapshot) => {
            const data = snapshot.val();
            const experiences = Array.isArray(data)
                ? data
                : (data ? Object.values(data) : []);
            this.setState({
                experiences,
                contentLoading: false
            });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        if (this.state.cropModalOpen) {
            this.setState({ cropModalOpen: false });
        } else if (this.state.modalOpen) {
            this.closeModal();
        }
    }

    // Same popup handles add and edit: null editingId = blank form for a
    // new experience, otherwise the form is pre-filled from that card/role
    // and saves back to its own Firebase key instead of creating a new one.
    openAddModal = () => {
        this.setState({
            editingId: null,
            draftExperience: { ...blankExperience },
            modalOpen: true,
            modalClosing: false
        });
    }

    openEditModal = (id, experience) => {
        this.setState({
            editingId: id,
            draftExperience: { ...blankExperience, ...experience },
            modalOpen: true,
            modalClosing: false
        });
    }

    closeModal = () => {
        // Match the exit-animation timing (see .experience-modal-* in
        // Experience.css) before actually unmounting.
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ modalOpen: false, modalClosing: false, editingId: null });
        }, 320);
    }

    openCropModal = () => {
        this.fileInput.click();
    };

    handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
            this.setState({
                uploadedImage: reader.result,
                // Kept so the crop/upload below can output the same
                // format instead of forcing everything through JPEG.
                uploadedImageType: file.type || 'image/jpeg',
                cropModalOpen: true,
                croppedAreaPixels: null, // reset crop area
                crop: { x: 0, y: 0 },
                zoom: 1
            });
            };
            reader.readAsDataURL(file);
        }

        // Allow re-uploading same file
        event.target.value = '';
    };

    onCropComplete = (_, croppedAreaPixels) => {
        this.setState({ croppedAreaPixels });
    };

    handleCropSave = async () => {
        const mimeType = this.state.uploadedImageType || 'image/jpeg';
        const croppedImage = await getCroppedImg(
            this.state.uploadedImage,
            this.state.croppedAreaPixels,
            mimeType
        );

        // Instant local preview in the modal while the upload happens in
        // the background - the field only actually gets written to
        // Firebase when Save/Add is clicked, same as every other field.
        this.handleDraftChange('cname', croppedImage);
        this.setState({
            cropModalOpen: false,
            uploadedImage: null,
            croppedAreaPixels: null
        });

        try {
            const blob = dataUrlToBlob(croppedImage);
            const fileName = `company-${Date.now()}.${extensionForMime(blob.type)}`;

            const { error: uploadError } = await supabase.storage
                .from(EXPERIENCE_BUCKET)
                .upload(fileName, blob, { contentType: blob.type, upsert: true });
            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from(EXPERIENCE_BUCKET).getPublicUrl(fileName);
            this.handleDraftChange('cname', data.publicUrl);
        } catch (error) {
            console.error('Error uploading company logo:', error);
            showToast(`Could not upload the logo: ${error.message || 'please try again.'}`, 'error');
        }
    };

    // Photo is optional - lets the admin clear one back out to the
    // fallback briefcase icon instead of being stuck once uploaded.
    removeImage = () => {
        this.handleDraftChange('cname', '');
    };

    handleDraftChange = (field, value) => {
        this.setState((prev) => ({
            draftExperience: { ...prev.draftExperience, [field]: value }
        }));
    }

    saveExperience = () => {
        const { cname, company, title, role, location, type, mode, start, end } = this.state.draftExperience;
        // Photo (cname) is optional - falls back to a briefcase icon.
        if (!title || !role || !location || !start || !end) return showToast('Fill in every field before saving.', 'error');
        update(experienceRef, {
            [this.state.editingId]: { cname: cname || '', company, title, role, location, type, mode, start, end }
        })
            .then(() => {
                this.closeModal();
                showToast('Experience updated', 'success');
            })
            .catch((error) => {
                console.error('Error updating experience:', error);
                showToast('Could not update this experience - please try again.', 'error');
            });
    }

    addNewExperience = () => {
        const { title, role, location, start, end } = this.state.draftExperience;
        // Photo (cname) is optional - falls back to a briefcase icon.
        if (!title || !role || !location || !start || !end) return showToast('Fill in every field before adding.', 'error');

        const id = this.state.experiences.length.toString(); // Firebase keys must be strings

        // Save using index as key
        set(child(experienceRef, id), this.state.draftExperience)
            .then(() => {
                this.closeModal();
                showToast('New experience added', 'success');
            })
            .catch(error => {
                console.error("Error saving experience:", error);
                showToast('Could not add this experience - please try again.', 'error');
            });
    }

    deleteExperience = () => {
        if (this.state.editingId === null) return;
        if (!window.confirm("Delete this experience? This can't be undone.")) return;

        // Grabbed before the Firebase record is gone - draftExperience is
        // already prefilled from this exact entry since the modal is open.
        const imagePath = storagePathFromUrl(this.state.draftExperience.cname, EXPERIENCE_BUCKET);

        remove(child(experienceRef, this.state.editingId.toString()))
            .then(async () => {
                if (imagePath) {
                    const { error } = await supabase.storage.from(EXPERIENCE_BUCKET).remove([imagePath]);
                    // The entry itself is already deleted either way - a
                    // leftover file in storage isn't worth blocking or
                    // re-alerting over, just log it.
                    if (error) console.error('Error deleting company logo from storage:', error);
                }
                this.closeModal();
                showToast('Experience deleted', 'success');
            })
            .catch((error) => {
                console.error("Error deleting experience:", error);
                showToast('Could not delete this experience - please try again.', 'error');
            });
    }

    handleModalSubmit = () => {
        if (this.state.editingId !== null) {
            this.saveExperience();
        } else {
            this.addNewExperience();
        }
    }

    render() {
        const { experiences, isLoggedIn, draftExperience, modalOpen, modalClosing, editingId } = this.state;
        const ordered = experiences
            .map((experience, id) => ({ experience, id }))
            .filter((entry) => entry.experience)
            .slice()
            .reverse();
        const groups = groupByCompany(ordered);

        return (
            <div className='base'>
                <div className='main-content-outer'>
                <h1 className="sr-only">Experience</h1>
                <span className="page-watermark" aria-hidden="true">Experience</span>
                {this.state.contentLoading && <PageLoader />}
                {!this.state.contentLoading && (
                <>
                <div className="experience-container">
                    {groups.map((group) => {
                        const entries = group.entries;
                        const multiRole = entries.length > 1;
                        const rep = entries.find((e) => e.experience.cname) || entries[0];
                        const companyLabel = entries[0].experience.company || entries[0].experience.title;
                        const { rangeText, duration, anyCurrent } = groupSpan(entries);
                        const cardIsCurrent = multiRole ? anyCurrent : isCurrentRole(entries[0].experience.end);
                        const singleClickable = isLoggedIn && !multiRole;
                        return (
                            <div
                                className={`experience-card${cardIsCurrent ? ' experience-card-current' : ''}${singleClickable ? ' experience-card-clickable' : ''}`}
                                key={group.key}
                                {...(singleClickable ? {
                                    onClick: () => this.openEditModal(entries[0].id, entries[0].experience),
                                    onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(entries[0].id, entries[0].experience)),
                                    role: 'button',
                                    tabIndex: 0,
                                    'aria-label': `Edit ${entries[0].experience.title}`
                                } : {})}
                            >
                                {cardIsCurrent && (
                                    <div className="experience-current-rail" aria-hidden="true">
                                        <span>Current</span>
                                    </div>
                                )}
                                <div className="experience-imgBx">
                                    {rep.experience.cname ? (
                                        <img src={rep.experience.cname} alt="" />
                                    ) : (
                                        <LuBriefcase className="experience-fallback-icon" />
                                    )}
                                </div>
                                <div className="experience-contentBx">
                                    {multiRole ? (
                                        <>
                                            <p className="experience-company-name">{companyLabel}</p>
                                            <div className="experience-dates-row">
                                                <span className="experience-dates">{rangeText}</span>
                                                {duration && <span className="experience-duration-tag">{duration}</span>}
                                            </div>
                                            <div className="experience-roles">
                                                {entries.map(({ experience, id }) => {
                                                    const current = isCurrentRole(experience.end);
                                                    const dur = roleDuration(experience.start, experience.end);
                                                    return (
                                                        <div
                                                            className={`experience-role-row${isLoggedIn ? ' experience-role-row-clickable' : ''}`}
                                                            key={id}
                                                            {...(isLoggedIn ? {
                                                                onClick: () => this.openEditModal(id, experience),
                                                                onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(id, experience)),
                                                                role: 'button',
                                                                tabIndex: 0,
                                                                'aria-label': `Edit ${experience.title}`
                                                            } : {})}
                                                        >
                                                            <span className="experience-role-dot" aria-hidden="true" />
                                                            <div className="experience-role-body">
                                                                <p className="experience-role-title">{experience.title}</p>
                                                                <div className="experience-dates-row">
                                                                    <span className="experience-dates">
                                                                        {experience.start} — {experience.end || 'Present'}
                                                                        {dur && ` · ${dur}`}
                                                                    </span>
                                                                    {current && <span className="experience-current-badge">Current</span>}
                                                                </div>
                                                                {(experience.type || experience.mode || experience.location) && (
                                                                    <p className="experience-role-meta">
                                                                        {[experience.type, experience.mode, experience.location].filter(Boolean).join(' · ')}
                                                                    </p>
                                                                )}
                                                                {experience.role && <p className="experience-role-text">{experience.role}</p>}
                                                                {isLoggedIn && <span className="experience-edit-hint"><LuPencilLine /> Edit</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (() => {
                                        const experience = entries[0].experience;
                                        const dur = roleDuration(experience.start, experience.end);
                                        return (
                                            <>
                                                <p className="experience-title-text">{experience.title}</p>
                                                {experience.company && <p className="experience-company-name">{experience.company}</p>}
                                                <div className="experience-dates-row">
                                                    <span className="experience-dates">
                                                        {experience.start} — {experience.end || 'Present'}
                                                        {dur && ` · ${dur}`}
                                                    </span>
                                                </div>
                                                {(experience.type || experience.mode || experience.location) && (
                                                    <p className="experience-role-meta">
                                                        {[experience.type, experience.mode, experience.location].filter(Boolean).join(' · ')}
                                                    </p>
                                                )}
                                                <p className="experience-role-text">{experience.role}</p>
                                                {singleClickable && <span className="experience-edit-hint"><LuPencilLine /> Edit</span>}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {isLoggedIn && (
                    <button
                        type="button"
                        className="experience-fab"
                        onClick={this.openAddModal}
                        aria-label="Add new experience"
                    >
                        <LuPlus />
                    </button>
                )}

                {this.state.modalOpen && (
                        <div
                            className={`experience-modal-backdrop${modalClosing ? ' is-closing' : ''}`}
                            onClick={this.closeModal}
                        >
                            <div className="experience-modal-panel" onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className="experience-modal-close"
                                    onClick={this.closeModal}
                                    aria-label="Close"
                                >
                                    <LuX />
                                </button>
                                <span className="experience-add-eyebrow">
                                    {editingId !== null ? <><LuPencilLine /> Editing</> : <><LuSparkles /> Admin</>}
                                </span>
                                <h3 className="experience-add-heading">
                                    {editingId !== null ? 'Edit experience' : 'Add new experience'}
                                </h3>
                                <div className="experience-edit-photo">
                                    <button
                                        type="button"
                                        className="experience-upload-tile"
                                        onClick={this.openCropModal}
                                        aria-label="Upload company logo"
                                    >
                                        {draftExperience.cname ? (
                                            <img src={draftExperience.cname} alt="" />
                                        ) : (
                                            <LuImagePlus className="experience-upload-icon" />
                                        )}
                                    </button>
                                    <div className="experience-upload-meta">
                                        <span className="experience-upload-label">Company logo</span>
                                        <span className="experience-upload-hint">Optional - shows a briefcase icon if left blank</span>
                                        {draftExperience.cname && (
                                            <button
                                                type="button"
                                                className="experience-upload-remove"
                                                onClick={this.removeImage}
                                            >
                                                <LuX /> Remove photo
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="experience-field-row">
                                    <label className="experience-field">
                                        <span className="experience-field-label"><LuBuilding2 /> Company</span>
                                        <input
                                            type="text"
                                            placeholder="Groups multiple roles together"
                                            value={draftExperience.company || ""}
                                            onChange={(e) => this.handleDraftChange("company", e.target.value)}
                                            className="experience-field-input"
                                        />
                                    </label>
                                    <label className="experience-field">
                                        <span className="experience-field-label">Job title</span>
                                        <input
                                            type="text"
                                            value={draftExperience.title}
                                            onChange={(e) => this.handleDraftChange("title", e.target.value)}
                                            className="experience-field-input"
                                            autoFocus
                                        />
                                    </label>
                                </div>
                                <label className="experience-field">
                                    <span className="experience-field-label">Role description</span>
                                    <textarea
                                        rows={3}
                                        placeholder="What did you do in this role?"
                                        value={draftExperience.role}
                                        onChange={(e) => this.handleDraftChange("role", e.target.value)}
                                        className="experience-field-input experience-field-textarea"
                                    />
                                </label>
                                <label className="experience-field">
                                    <span className="experience-field-label"><LuMapPin /> Location</span>
                                    <input
                                        type="text"
                                        value={draftExperience.location}
                                        onChange={(e) => this.handleDraftChange("location", e.target.value)}
                                        className="experience-field-input"
                                    />
                                </label>
                                <div className="experience-field-row">
                                    <label className="experience-field">
                                        <span className="experience-field-label">Type</span>
                                        <input
                                            type="text"
                                            placeholder="Full-time, Internship..."
                                            value={draftExperience.type || ""}
                                            onChange={(e) => this.handleDraftChange("type", e.target.value)}
                                            className="experience-field-input"
                                        />
                                    </label>
                                    <label className="experience-field">
                                        <span className="experience-field-label">Mode</span>
                                        <input
                                            type="text"
                                            placeholder="Remote, On-site..."
                                            value={draftExperience.mode || ""}
                                            onChange={(e) => this.handleDraftChange("mode", e.target.value)}
                                            className="experience-field-input"
                                        />
                                    </label>
                                </div>
                                <div className="experience-field-row">
                                    <label className="experience-field">
                                        <span className="experience-field-label"><LuCalendar /> Start</span>
                                        <input
                                            type="text"
                                            value={draftExperience.start}
                                            onChange={(e) => this.handleDraftChange("start", e.target.value)}
                                            className="experience-field-input"
                                        />
                                    </label>
                                    <label className="experience-field">
                                        <span className="experience-field-label"><LuCalendar /> End</span>
                                        <input
                                            type="text"
                                            placeholder="Present"
                                            value={draftExperience.end}
                                            onChange={(e) => this.handleDraftChange("end", e.target.value)}
                                            className="experience-field-input"
                                        />
                                    </label>
                                </div>
                                <div className="experience-modal-actions">
                                    <button
                                        onClick={this.handleModalSubmit}
                                        className="app-form-button experience-save-cta"
                                    >
                                        {editingId !== null ? <><LuSave /> Save changes</> : <><LuPlus /> Add experience</>}
                                    </button>
                                    {editingId !== null && (
                                        <button
                                            type="button"
                                            onClick={this.deleteExperience}
                                            className="experience-delete-cta"
                                        >
                                            <LuTrash2 /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {this.state.cropModalOpen && (
                    <div className="crop-modal-backdrop" onClick={() => this.setState({ cropModalOpen: false })}>
                    <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
                        <span className="crop-modal-eyebrow">Adjust logo</span>
                        <div className="cropper-wrapper">
                        <Cropper
                            image={this.state.uploadedImage}
                            crop={this.state.crop}
                            zoom={this.state.zoom}
                            aspect={3}
                            cropShape="rect" // or "round"
                            showGrid={true}
                            onCropChange={(crop) => this.setState({ crop })}
                            onZoomChange={(zoom) => this.setState({ zoom })}
                            onCropComplete={this.onCropComplete}
                        />
                        </div>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={this.state.zoom}
                            onChange={(e) => this.setState({ zoom: parseFloat(e.target.value) })}
                            className="zoom-slider"
                        />
                        <div className="crop-actions">
                        <button onClick={this.handleCropSave}>Save crop</button>
                        <button onClick={() => this.setState({ cropModalOpen: false })}>Cancel</button>
                        </div>
                    </div>
                    </div>
                    )}

                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={(ref) => (this.fileInput = ref)}
                    onChange={this.handleFileChange}
                />
                </>
                )}
                </div>
            </div>
        )
    }
}
